import { useState } from "react";
import { useTranslation } from "react-i18next";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import IconButton from "@material-ui/core/IconButton";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import Chip from "@material-ui/core/Chip";
import dayjs from "dayjs";
import utc from "dayjs-plugin-utc";
import AlertDialog from "../AlertDialog";

dayjs.extend(utc);

const TEMPLATE_TYPE_LABELS = {
  nudge: "Nudge",
  evaluation: "Evaluation",
  summarization: "Summarization",
  custom: "Custom",
};

const TEMPLATE_TYPE_COLORS = {
  nudge: "#1976d2",
  evaluation: "#388e3c",
  summarization: "#7b1fa2",
  custom: "#f57c00",
};

export default function PromptTemplateTable({
  templates,
  isLoading,
  onEdit,
  onDelete,
}) {
  const { t } = useTranslation();
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });

  const handleDeleteClick = id => {
    setDeleteDialog({ open: true, id });
  };

  const handleDeleteConfirm = () => {
    onDelete(deleteDialog.id);
    setDeleteDialog({ open: false, id: null });
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({ open: false, id: null });
  };

  const columns = [
    { id: "name", label: "Name", width: "25%" },
    { id: "template_type", label: "Type", width: "15%" },
    { id: "description", label: "Description", width: "25%" },
    { id: "is_active", label: "Status", width: "10%" },
    { id: "created_at", label: "Created At", width: "15%" },
    { id: "actions", label: "Actions", width: "10%" },
  ];

  return (
    <>
      <TableContainer component={Paper} elevation={0}>
        <Table aria-label="Prompt Templates">
          <TableHead>
            <TableRow>
              {columns.map(column => (
                <TableCell key={column.id} style={{ width: column.width }}>
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="center">
                  Loading...
                </TableCell>
              </TableRow>
            ) : templates.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="center">
                  No templates found
                </TableCell>
              </TableRow>
            ) : (
              templates.map(template => (
                <TableRow key={template.id} hover>
                  <TableCell>{template.name}</TableCell>
                  <TableCell>
                    <Chip
                      label={TEMPLATE_TYPE_LABELS[template.template_type] || template.template_type}
                      style={{
                        backgroundColor: TEMPLATE_TYPE_COLORS[template.template_type] || "#9e9e9e",
                        color: "white",
                      }}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{template.description || "-"}</TableCell>
                  <TableCell>
                    <Chip
                      label={template.is_active === 1 ? "Active" : "Inactive"}
                      color={template.is_active === 1 ? "primary" : "default"}
                      variant="outlined"
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {template.created_at ? dayjs(template.created_at).format("YYYY-MM-DD HH:mm") : "-"}
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => onEdit(template)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDeleteClick(template.id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <AlertDialog
        open={deleteDialog.open}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title={t("promptTemplate.deleteTitle") || "Delete Template"}
        message={t("promptTemplate.deleteMessage") || "Are you sure you want to delete this template?"}
        confirmText={t("common.delete") || "Delete"}
        cancelText={t("common.cancel") || "Cancel"}
      />
    </>
  );
}